import {
  AddressFormInputs,
  Button,
  Input,
  InputAutocomplete,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import { useMe, useQuery, useTeam } from '@vrplatform/data';
import { Icon, OfficeIcon, UserIcon } from '@vrplatform/icons';
import type { SelectItem } from '@vrplatform/components';
import { useAppName } from '@vrplatform/theme';
import { sortBy } from '@vrplatform/utils';
import {
  type AutocompleteProps,
  Box,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  Tooltip,
  rem,
} from '@mantine/core';
import { Text } from '@mantine/core';
import { type account, defaultAccountOrderBy } from '@vrplatform/graphql';
import {
  OWNER_COMPANY_TYPES,
  type OwnerCompanyTypeValue,
  type formatAddress,
  formatUserName,
  validateTin,
  whereAccounts,
} from '@vrplatform/ui-common';
import {
  Controller,
  FormProvider,
  type UseFormReturn,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { validateEmail } from '../../utils';
import { DrawerCollapsableSection } from '../_components';

export const STORYBOOK_OWNER_EDIT_FORM_DEFAULT_ACCOUNT_QUERY_KEY =
  'owner-edit-form-default-account';
export const STORYBOOK_OWNER_EDIT_FORM_EMAIL_SUGGESTIONS_QUERY_KEY =
  'owner-edit-form-email-suggestions';

export type OwnerEditFormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  taxId: string;
  defaultAccountId: string | null;
  type: 'individual' | OwnerCompanyTypeValue;
  address: Parameters<typeof formatAddress>[0];
  addUserOption: boolean;
};

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';

export const OwnerEditForm = ({
  handleSubmit,
  initialValues,
  onReset,
  submitButtonLabel,
  isEmailDisabled,
  isOwnerAddModal,
}: {
  handleSubmit: (
    values: OwnerEditFormInputs,
    methods: UseFormReturn<OwnerEditFormInputs, any>
  ) => Promise<any>;
  onReset: () => void;
  initialValues: OwnerEditFormInputs | undefined;
  submitButtonLabel: string;
  isEmailDisabled?: boolean;
  isOwnerAddModal: boolean;
}) => {
  const methods = useForm<OwnerEditFormInputs>({
    values: initialValues,
  });

  const ownerType =
    useWatch({
      control: methods.control,
      name: 'type',
    }) ?? 'individual';

  const isIndividual = ownerType === 'individual';

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        sx={() => ({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflowY: 'hidden',
          overflowX: 'unset',
        })}
        onSubmit={methods.handleSubmit((values) =>
          handleSubmit(values, methods)
        )}
        onReset={onReset}
      >
        <ScrollArea
          sx={{
            flex: 1,
          }}
        >
          <Stack mt="sm" gap="lg" pb="sm">
            <Stack pb="sm">
              <Controller
                control={methods.control}
                defaultValue="individual"
                name="type"
                rules={{
                  required: 'Owner type is required',
                }}
                render={({ field, fieldState: { error } }) => {
                  const individualOption = {
                    label: 'Individual',
                    value: 'individual',
                    icon: <UserIcon size={16} />,
                  };

                  const options = [
                    individualOption,
                    ...OWNER_COMPANY_TYPES.map((x) => ({
                      ...x,
                      icon: <OfficeIcon size={16} />,
                    })),
                  ];

                  return (
                    <Box>
                      <InputWrapper
                        label="Owner type"
                        required
                        error={error?.message}
                      >
                        <InputSelect
                          data={{
                            options,
                            sort: null,
                          }}
                          type="single"
                          value={
                            options.find((x) => x.value === field.value) || null
                          }
                          inputProps={{
                            placeholder: 'Company type',
                            error: !!error,
                          }}
                          dropdownProps={{ width: 'target' }}
                          setValue={(e) => {
                            if (e?.value) {
                              if (
                                (e.value === 'individual' &&
                                  field.value !== 'individual') ||
                                (e.value !== 'individual' &&
                                  field.value === 'individual')
                              ) {
                                methods.setValue('firstName', '');
                                methods.setValue('lastName', '');
                                methods.clearErrors('firstName');
                                methods.clearErrors('lastName');
                              }
                              field.onChange(e.value);
                            }
                          }}
                        />
                      </InputWrapper>
                    </Box>
                  );
                }}
              />

              {!isIndividual ? (
                <Controller
                  control={methods.control}
                  name="lastName"
                  defaultValue=""
                  rules={{
                    required: 'Company name is required',
                    validate: trapSpacesForRequiredFields,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <InputWrapper
                      label="Company name"
                      error={error?.message}
                      required
                    >
                      <Input
                        {...field}
                        placeholder="Company name"
                        autoComplete="off"
                        error={!!error}
                      />
                    </InputWrapper>
                  )}
                />
              ) : (
                <Box
                  sx={(theme) => ({
                    alignItems: 'flex-start',
                    justifyContent: 'stretch',
                    display: 'flex',
                    gap: theme.spacing.md,
                    '> div': {
                      flex: 1,
                    },
                  })}
                >
                  <Controller
                    control={methods.control}
                    name="firstName"
                    defaultValue=""
                    rules={{
                      required: 'First name is required',
                      validate: trapSpacesForRequiredFields,
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <InputWrapper
                        label="First name"
                        error={error?.message}
                        required={true}
                      >
                        <Input
                          {...field}
                          placeholder="First name"
                          autoComplete="off"
                          error={!!error}
                        />
                      </InputWrapper>
                    )}
                  />
                  <Controller
                    control={methods.control}
                    name="lastName"
                    defaultValue=""
                    rules={{
                      required: 'Last name is required',
                      validate: trapSpacesForRequiredFields,
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <InputWrapper
                        label="Last name"
                        error={error?.message}
                        required={true}
                      >
                        <Input
                          {...field}
                          placeholder="Last name"
                          autoComplete="off"
                          error={!!error}
                        />
                      </InputWrapper>
                    )}
                  />
                </Box>
              )}

              <OwnerEmailInput isEmailDisabled={!!isEmailDisabled} />

              {isOwnerAddModal && (
                <Controller
                  control={methods.control}
                  name="addUserOption"
                  defaultValue={false}
                  render={({ field }) => (
                    <InputWrapper
                      label="Owner portal access"
                      sx={{
                        flexDirection: 'column',
                        display: 'flex',
                      }}
                      description="Grant the owner access to the portal where they can view property details, financial statements, and communicate directly with the management team."
                      inputWrapperOrder={[
                        'label',
                        'input',
                        'description',
                        'error',
                      ]}
                    >
                      <SegmentedControl
                        data={[
                          {
                            label: 'Yes, send invite',
                            value: 'invite',
                          },
                          {
                            label: 'Do not invite',
                            value: 'no-invite',
                          },
                        ]}
                        size="xs"
                        my={rem(5)}
                        data-testid="invite-user-checkbox"
                        value={field.value ? 'invite' : 'no-invite'}
                        onChange={(value) => {
                          field.onChange(value === 'invite');
                          methods.clearErrors('email');
                        }}
                      />
                    </InputWrapper>
                  )}
                />
              )}
            </Stack>

            <DrawerCollapsableSection title="Statements">
              <Controller
                control={methods.control}
                name="phone"
                render={({ field, fieldState: { error } }) => (
                  <InputWrapper label="Phone" error={error?.message} mb="sm">
                    <Input
                      {...field}
                      placeholder="Phone"
                      type="tel"
                      autoComplete="off"
                      error={!!error}
                    />
                  </InputWrapper>
                )}
              />

              <Box pb="sm">
                <AddressFormInputs disableType="country-select" />
              </Box>
            </DrawerCollapsableSection>

            <DrawerCollapsableSection title="Tax information">
              <Controller
                control={methods.control}
                name="taxId"
                rules={{
                  validate: (value, formValues) => {
                    const isUSA =
                      formValues.address?.countryCode?.toLowerCase() === 'us';

                    if (value?.trim() && isUSA) {
                      const isError = typeof validateTin(value) === 'string';

                      return isError
                        ? 'Please enter a valid 9-digit US TIN or leave the input blank'
                        : true;
                    }

                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <InputWrapper label="TIN " error={error?.message} mb="sm">
                    <Input
                      {...field}
                      placeholder="Tax Identification Number"
                      autoComplete="off"
                      error={!!error}
                    />
                  </InputWrapper>
                )}
              />
            </DrawerCollapsableSection>

            <DrawerCollapsableSection title="Transfer bank account">
              <Controller
                control={methods.control}
                name="defaultAccountId"
                render={({ field, fieldState }) => {
                  const [{ id: teamId }] = useTeam();

                  const {
                    data,
                    isLoading: loadingQuery,
                    error,
                  } = useQuery(
                    (q, args) => {
                      const getAccount = (acc: account): SelectItem => ({
                        value: acc.id,
                        label: acc.title || 'No name',
                      });

                      const accounts = q
                        .accounts({
                          where: whereAccounts({
                            tenantId: args.teamId,
                            type: 'bank',
                          }),
                          order_by: defaultAccountOrderBy,
                        })
                        .map(getAccount);

                      const defaultAccount =
                        q
                          .accounts({
                            where: {
                              tenantId: { _eq: args.teamId },
                              assignments: {
                                type: { _eq: 'payout_bankAccount' },
                                tenantId: { _eq: args.teamId },
                              },
                            },
                            order_by: defaultAccountOrderBy,
                          })
                          .map(getAccount)[0] ?? null;

                      return {
                        accounts,
                        defaultAccount,
                      };
                    },
                    {
                      queryKey: ['accounts'],
                      variables: {
                        teamId,
                      },
                      storybookQueryKey:
                        STORYBOOK_OWNER_EDIT_FORM_DEFAULT_ACCOUNT_QUERY_KEY,
                    }
                  );

                  const value =
                    data?.accounts.find((o) => o.value === field.value) || null;

                  const defaultAccount = data?.defaultAccount;

                  return (
                    <InputWrapper
                      label="Transfer bank account"
                      description="You can optionally override the default bank account for this owner. The bank account will be used to transfer funds to the owner."
                      error={error?.message}
                      pb="sm"
                      inputWrapperOrder={[
                        'label',
                        'input',
                        'description',
                        'error',
                      ]}
                      styles={{
                        description: {
                          marginTop: rem(4),
                        },
                      }}
                    >
                      <InputSelect
                        type="single"
                        value={value}
                        setValue={(v) => field.onChange(v?.value || null)}
                        data={{
                          options: data?.accounts ?? [],
                          loading: loadingQuery,
                          error,
                          sort: null,
                        }}
                        inputProps={{
                          placeholder: `Default: ${defaultAccount?.label ?? '-'}`,
                          loadingQuery,
                          withClearButton: true,
                          error: fieldState.error?.message,
                        }}
                        dropdownProps={{
                          width: 'target',
                        }}
                      />
                    </InputWrapper>
                  );
                }}
              />
            </DrawerCollapsableSection>
          </Stack>
        </ScrollArea>

        <Group
          sx={(theme) => ({
            borderTop: `1px solid ${theme.colors.gray[2]}`,
            paddingTop: theme.spacing.sm,
            paddingBottom: theme.spacing.sm,
          })}
        >
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            sx={{ flexGrow: '1!important' as any }}
            type="submit"
            data-testid="submit"
            variant="primary"
            loading={methods.formState.isSubmitting}
          >
            {submitButtonLabel}
          </Button>
        </Group>
      </Box>
    </FormProvider>
  );
};

function useEmailSuggestionsInputQuery() {
  const [{ id: teamId }] = useTeam();
  const { id: meId } = useMe();

  return useQuery(
    (q, args) => {
      const memberships = q
        .tenantUser({
          where: {
            user: {
              email: { _is_null: false },
            },
            _or: [
              {
                tenant: {
                  members: {
                    userId: { _eq: args.meId },
                  },
                },
              },
              {
                tenantId: { _eq: args.teamId },
              },
            ],
          },
          order_by: [
            {
              user: {
                email: 'asc_nulls_last',
              },
            },
          ],
        })
        .map((x) => ({
          role: x.role as 'admin' | 'user' | 'owner',
          email: x.user.email?.toLowerCase()!,
          tenantId: x.tenantId,
          name: formatUserName(x.user, {
            lastNameFirst: false,
            showEmpty: true,
          }),
        }));

      const propertyManager = memberships.filter((x) => x.role !== 'owner');
      const owner = memberships.filter(
        (x) => x.role === 'owner' && x.tenantId === args.teamId
      );

      return {
        owner: sortBy([...new Set(owner.map((x) => x.email))]),
        propertyManager: [...new Set(propertyManager.map((x) => x.email))],
        memberships,
      };
    },
    {
      variables: {
        teamId,
        meId,
      },
      storybookQueryKey: STORYBOOK_OWNER_EDIT_FORM_EMAIL_SUGGESTIONS_QUERY_KEY,
    }
  );
}

const renderAutocompleteOption: AutocompleteProps['renderOption'] = ({
  option,
}) => {
  const email = option.value;

  const queryData = useEmailSuggestionsInputQuery();

  const memberships = queryData.data?.memberships ?? [];
  const user = memberships.find((x) => x.email === email);

  return (
    <div>
      <Text size="sm">{user?.name}</Text>
      <Text size="xs" opacity={0.5}>
        {email}
      </Text>
    </div>
  );
};

const PM_ERROR_MESSAGE =
  'This email belongs to a property manager. Please use a different email';

export const OwnerEmailInput = ({
  isEmailDisabled,
  forceRequired,
}: {
  isEmailDisabled: boolean;
  forceRequired?: boolean;
}) => {
  const { appName } = useAppName();

  const methods = useFormContext<OwnerEditFormInputs>();

  const isInvite = useWatch({
    control: methods.control,
    name: 'addUserOption',
    defaultValue: false,
  });

  const isRequired = forceRequired ?? isInvite;

  const queryData = useEmailSuggestionsInputQuery();

  return (
    <Controller
      control={methods.control}
      name="email"
      rules={{
        validate: (v) => {
          const email = v?.trim().toLowerCase() || '';

          if (
            email &&
            queryData.data?.propertyManager.some((x) => x === email)
          ) {
            return PM_ERROR_MESSAGE;
          }

          const emailValidation = validateEmail(email, {
            required: isRequired,
            requiredMessage: isInvite
              ? `Email is required when trying to invite an owner to ${appName}`
              : 'Email is required',
            invalidMessage: isRequired
              ? 'Please enter a valid email'
              : 'Please enter a valid email or leave the field empty',
          });

          if (emailValidation !== true) return emailValidation;

          return true;
        },
      }}
      render={({ field, fieldState }) => {
        const data = queryData.data?.owner ?? [];
        const isPerfectMatch = !!data.find((x) => x === field.value);

        const isPropertyManager = !!queryData.data?.propertyManager.find(
          (x) => x === field.value
        );

        const error = !!fieldState.error || isPropertyManager;

        return (
          <InputWrapper
            label={
              <Group display="inline-flex" wrap="nowrap" gap={rem(6)}>
                <Text inherit span>
                  Email
                </Text>
                {isEmailDisabled && (
                  <Tooltip
                    multiline
                    maw={400}
                    label="Once your owner has accessed the owner portal, only they are able to edit their email. If the owner lost access to their email, you can revoke their access to the owner portal and add a new email."
                  >
                    <Icon icon="InfoIcon" size={14} />
                  </Tooltip>
                )}
              </Group>
            }
            error={fieldState.error?.message}
            required={isRequired}
          >
            <Tooltip
              label={PM_ERROR_MESSAGE}
              disabled={!isPropertyManager}
              withArrow
            >
              <InputAutocomplete
                ref={field.ref}
                data={data}
                name={field.name}
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Email"
                type="email"
                error={!!error}
                disabled={isEmailDisabled}
                debounce={0}
                rightSection={
                  isPropertyManager && (
                    <Icon icon="AlertCircleIcon" size={14} color={'red'} />
                  )
                }
                renderOption={renderAutocompleteOption}
                dropdownOpened={!!field.value?.trim() && !isPerfectMatch}
              />
            </Tooltip>
          </InputWrapper>
        );
      }}
    />
  );
};
